/**
 * USPS packaging a seller can pick for a listing, as Shippo names it.
 *
 * Each entry is a Shippo *carrier parcel template*: a box or envelope whose
 * dimensions the carrier defines, so a shipment quoted with `template` plus a
 * weight is priced at that packaging's own rate — which for a Flat Rate box is
 * the flat rate, not a dimensional one. The sell form used to store the box's
 * inches and send those, so a "Medium Flat Rate Box" was quoted as an 11x8x5
 * parcel at Priority Mail's weight-and-zone price and the flat rate never
 * appeared anywhere.
 *
 * The tokens are the ones `GET /parcel-templates?carrier=usps` returns today
 * (fifteen; the SDK enum also lists Regional Rate boxes and a gift-card
 * envelope USPS has discontinued). Three are listed but not offered:
 * `USPS_Card` and `USPS_LargeVideoFlatRateBox` return no domestic rates on a
 * Shippo account, and `USPS_APOFlatRateBox` only prices to military addresses.
 * Keeping them here means a token stored by an older client, or an import,
 * still resolves to a name instead of being treated as garbage.
 *
 * Dimensions are USPS's published ones in inches, kept for display and for
 * filling the listing's dimension columns so every reader that predates
 * `parcelTemplate` keeps working.
 */

export type UspsParcelTemplateGroup = 'envelope' | 'box' | 'other';

export interface UspsParcelTemplate {
  /** Shippo's token, stored verbatim in SUPPLY_LISTINGS.parcelTemplate. */
  token: string;
  /** What the seller sees. */
  name: string;
  group: UspsParcelTemplateGroup;
  /** One line on what fits. */
  fits: string;
  /** Outside dimensions in inches, as USPS publishes them. 0 means "you decide". */
  length: number;
  width: number;
  height: number;
  /** Whether Shippo returns domestic USPS rates for it on a Shippo account. */
  offered: boolean;
  /** The picture on its card, under public/images/shippers (mirrored to the CDN). */
  image?: string;
  /** One price whatever the weight (to 70 lb) — the card needs no weight from the seller. */
  flatRate: boolean;
  /**
   * The size as USPS prints it on the box, for a card: longest side first, the
   * way the old presets read (Width 8 5/8 · Height 5 3/8 · Depth 1 5/8).
   */
  display?: { width: string; height: string; depth: string };
}

export const USPS_PARCEL_TEMPLATES: readonly UspsParcelTemplate[] = [
  // Flat Rate envelopes — all Priority Mail, one price whatever the weight (to 70 lb).
  { token: 'USPS_FlatRateEnvelope', name: 'Flat Rate Envelope', group: 'envelope', fits: 'Documents, cards, thin items', length: 12.5, width: 9.5, height: 0.75, offered: true, display: { width: '12 1/2', height: '9 1/2', depth: '3/4' }, flatRate: true, image: 'flatrate-envelope.svg' },
  { token: 'USPS_FlatRateLegalEnvelope', name: 'Flat Rate Legal Envelope', group: 'envelope', fits: 'Legal-size paper, long thin items', length: 15, width: 9.5, height: 0.75, offered: true, display: { width: '15', height: '9 1/2', depth: '3/4' }, flatRate: true, image: 'flatrate-legal-envelope.svg' },
  { token: 'USPS_FlatRatePaddedEnvelope', name: 'Flat Rate Padded Envelope', group: 'envelope', fits: 'Clothing, soft goods, small electronics', length: 12.5, width: 9.5, height: 1, offered: true, display: { width: '12 1/2', height: '9 1/2', depth: '1' }, flatRate: true, image: 'flatrate-padded-envelope.svg' },
  { token: 'USPS_FlatRateCardboardEnvelope', name: 'Flat Rate Cardboard Envelope', group: 'envelope', fits: 'Photos, prints, anything that must stay flat', length: 12.5, width: 9.5, height: 0.75, offered: true, display: { width: '12 1/2', height: '9 1/2', depth: '3/4' }, flatRate: true, image: 'flatrate-cardboard-envelope.svg' },
  { token: 'USPS_FlatRateWindowEnvelope', name: 'Flat Rate Window Envelope', group: 'envelope', fits: 'Documents with the address showing through', length: 10, width: 5, height: 0.75, offered: true, display: { width: '10', height: '5', depth: '3/4' }, flatRate: true, image: 'flatrate-window-envelope.svg' },
  { token: 'USPS_SmallFlatRateEnvelope', name: 'Small Flat Rate Envelope', group: 'envelope', fits: 'Jewelry, cards, very small items', length: 10, width: 6, height: 0.8, offered: true, display: { width: '10', height: '6', depth: '3/4' }, flatRate: true, image: 'flatrate-small-envelope.svg' },

  // Flat Rate boxes.
  { token: 'USPS_SmallFlatRateBox', name: 'Small Flat Rate Box', group: 'box', fits: 'Jewelry, electronics, tickets, small items', length: 8.6875, width: 5.4375, height: 1.75, offered: true, image: 'flatrate-small-box.svg', display: { width: '8 5/8', height: '5 3/8', depth: '1 5/8' }, flatRate: true },
  { token: 'USPS_MediumFlatRateBox1', name: 'Medium Flat Rate Box 1', group: 'box', fits: 'Top-loading. Shoes, clothes, game consoles', length: 11.25, width: 8.75, height: 6, offered: true, image: 'flatrate-medium-box-1.svg', display: { width: '11 1/4', height: '8 3/4', depth: '6' }, flatRate: true },
  { token: 'USPS_MediumFlatRateBox2', name: 'Medium Flat Rate Box 2', group: 'box', fits: 'Side-loading. Laptops, books, gifts', length: 14, width: 12, height: 3.5, offered: true, image: 'flatrate-medium-box-2.svg', display: { width: '14', height: '12', depth: '3 1/2' }, flatRate: true },
  { token: 'USPS_LargeFlatRateBox', name: 'Large Flat Rate Box', group: 'box', fits: 'Tools, large decor, jackets', length: 12.25, width: 12.25, height: 6, offered: true, image: 'flatrate-large-box.svg', display: { width: '12 1/4', height: '12 1/4', depth: '6' }, flatRate: true },
  { token: 'USPS_APOFlatRateBox', name: 'APO/FPO/DPO Flat Rate Box', group: 'box', fits: 'Military addresses only', length: 12.25, width: 12, height: 6, offered: false, image: 'flatrate-large-box.svg', flatRate: true },

  // Other USPS packaging — priced by weight and zone rather than flat.
  { token: 'USPS_SoftPack', name: 'Soft Pack (poly mailer)', group: 'other', fits: 'Clothing and soft goods in your own mailer', length: 0, width: 0, height: 0, offered: true, flatRate: false, image: 'softpack.svg' },
  { token: 'USPS_IrregularParcel', name: 'Irregular Parcel', group: 'other', fits: 'Tubes, rolls, odd shapes', length: 4, width: 4, height: 4, offered: true, display: { width: '4', height: '4', depth: '4' }, flatRate: false, image: 'irregular-parcel.svg' },
  { token: 'USPS_LargeVideoFlatRateBox', name: 'Large Video Flat Rate Box', group: 'other', fits: 'International only', length: 9.6, width: 6.4, height: 2.2, offered: false, flatRate: true },
  { token: 'USPS_Card', name: 'Card', group: 'other', fits: 'Postcards', length: 6, width: 4.25, height: 0.01, offered: false, flatRate: false },
] as const;

/**
 * The four boxes the sell form shows as cards, in the order it always has —
 * the packaging most listings actually go in. Everything else offered sits
 * behind the "More USPS packaging" card.
 */
export const FEATURED_USPS_PARCEL_TEMPLATES: readonly string[] = [
  'USPS_SmallFlatRateBox',
  'USPS_MediumFlatRateBox1',
  'USPS_MediumFlatRateBox2',
  'USPS_LargeFlatRateBox',
];

/** The value a listing carries in the form when the seller entered their own dimensions. */
export const CUSTOM_PARCEL = '';

/**
 * What the database carries for that choice. It has to differ from NULL: NULL
 * is a listing that never chose — every listing from before packaging existed,
 * every import — and those quote in the seller's default packaging when they
 * fit. A seller who picked their own box over that default meant it.
 */
export const CUSTOM_PARCEL_STORED = 'custom';

const BY_TOKEN: ReadonlyMap<string, UspsParcelTemplate> = new Map(
  USPS_PARCEL_TEMPLATES.map((template) => [template.token, template]),
);

/** The template for a token, or null for custom dimensions and unknown values. */
export function getUspsParcelTemplate(token: string | null | undefined): UspsParcelTemplate | null {
  if (!token) return null;
  return BY_TOKEN.get(token) ?? null;
}

/** Whether a stored value names packaging Shippo will quote for us today. */
export function isOfferedUspsParcelTemplate(token: string | null | undefined): boolean {
  return getUspsParcelTemplate(token)?.offered === true;
}

/** The templates a seller may choose, in display order. */
export function offeredUspsParcelTemplates(): UspsParcelTemplate[] {
  return USPS_PARCEL_TEMPLATES.filter((template) => template.offered);
}

/** The offered templates that are not one of the four featured boxes. */
export function moreUspsParcelTemplates(): UspsParcelTemplate[] {
  return offeredUspsParcelTemplates().filter((template) => !FEATURED_USPS_PARCEL_TEMPLATES.includes(template.token));
}

/** Whether a token is offered but lives behind the "More" card rather than on its own. */
export function isMoreUspsParcelTemplate(token: string | null | undefined): boolean {
  return isOfferedUspsParcelTemplate(token) && !FEATURED_USPS_PARCEL_TEMPLATES.includes(token as string);
}

/**
 * The token to persist for a client-supplied value: a known, offered template
 * or null. Anything else — an empty string, a discontinued token, a typo — is
 * stored as "custom dimensions" rather than as a value no quote could use.
 */
export function normalizeParcelTemplate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const token = value.trim();
  return isOfferedUspsParcelTemplate(token) ? token : null;
}

/**
 * The same for a listing, where "my own box" is a choice worth keeping: an
 * empty string (the form's `CUSTOM_PARCEL`) or the stored marker persists as
 * `CUSTOM_PARCEL_STORED`. Absent stays null — the listing never chose.
 */
export function normalizeListingParcelTemplate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const token = value.trim();
  if (token === CUSTOM_PARCEL || token === CUSTOM_PARCEL_STORED) return CUSTOM_PARCEL_STORED;
  return normalizeParcelTemplate(token);
}

/**
 * The dimension columns a template implies, rounded up to whole inches the way
 * the sell form always stored them, or null where USPS publishes none (Soft
 * Pack takes whatever the seller's mailer measures).
 */
export function dimensionsForParcelTemplate(
  token: string | null | undefined,
): { height: number; width: number; depth: number } | null {
  const template = getUspsParcelTemplate(token);
  if (!template || !(template.length > 0 && template.width > 0 && template.height > 0)) return null;
  return {
    depth: Math.ceil(template.length),
    width: Math.ceil(template.width),
    height: Math.ceil(template.height),
  };
}

export const USPS_PARCEL_TEMPLATE_GROUP_LABELS: Record<UspsParcelTemplateGroup, string> = {
  envelope: 'USPS Flat Rate envelopes',
  box: 'USPS Flat Rate boxes',
  other: 'Other USPS packaging',
};
